// Google Analytics Event Tracking Utility

declare global {
  interface Window {
    gtag?: (command: string, ...args: any[]) => void;
  }
}

export interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

/**
 * Track a custom event in Google Analytics
 */
export const trackEvent = ({ category, action, label, value }: AnalyticsEvent) => {
  // Only track if user has consented to analytics
  const consent = localStorage.getItem("cookie-consent");
  
  if (consent === "analytics-accepted" && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

/**
 * Track page views
 */
export const trackPageView = (pagePath: string, pageTitle: string) => {
  const consent = localStorage.getItem("cookie-consent");
  
  if (consent === "analytics-accepted" && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
};

// Pre-defined event tracking functions for common actions

export const trackProfileCreation = (profileId: string) => {
  trackEvent({
    category: 'Profile',
    action: 'profile_created',
    label: profileId,
  });
};

export const trackQRCodeDownload = (profileId: string, format: 'png' | 'pdf') => {
  trackEvent({
    category: 'QR Code',
    action: 'qr_code_downloaded',
    label: `${profileId}_${format}`,
  });
};

export const trackProfileScan = (profileId: string) => {
  trackEvent({
    category: 'Profile',
    action: 'profile_scanned',
    label: profileId,
  });
};

export const trackProfileView = (profileId: string) => {
  trackEvent({
    category: 'Profile',
    action: 'profile_viewed',
    label: profileId,
  });
};

export const trackAuthAction = (action: 'login' | 'signup' | 'logout') => {
  trackEvent({
    category: 'Authentication',
    action: `user_${action}`,
  });
};

export const trackSubscriptionAction = (action: 'upgrade_clicked' | 'checkout_initiated' | 'subscription_completed', plan?: string) => {
  trackEvent({
    category: 'Subscription',
    action,
    label: plan,
  });
};

export const trackButtonClick = (buttonName: string, location: string) => {
  trackEvent({
    category: 'Button Click',
    action: 'button_clicked',
    label: `${location}_${buttonName}`,
  });
};

export const trackFormSubmission = (formName: string, success: boolean) => {
  trackEvent({
    category: 'Form',
    action: success ? 'form_submitted' : 'form_error',
    label: formName,
  });
};

export const trackNavigationClick = (destination: string, source: string) => {
  trackEvent({
    category: 'Navigation',
    action: 'navigation_clicked',
    label: `${source}_to_${destination}`,
  });
};
